import { ReviewsBlock } from "../components/ReviewsWidget";
import { RelatedHealthArticles } from "../components/HealthInsightsSection";
import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import HomeCollectionSlotPicker from "../components/HomeCollectionSlotPicker";
import CouponInput from "../components/CouponInput";
import usePriceQuote, { MemberDiscountLine } from "../lib/usePriceQuote";
import { 
  Package, 
  CheckCircle2, 
  Clock, 
  Sparkles, 
  ShieldCheck, 
  Percent, 
  Home, 
  Building2, 
  User, 
  Phone, 
  Mail, 
  CreditCard,
  AlertCircle,
  Loader2,
  X
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { startRazorpayPayment, getBookingIntent } from "../lib/razorpayCheckout";
import { BookingPaymentOutcome } from "../components/BookingPaymentOutcome";
import { useLiveEvents } from "../lib/useLiveEvents";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;

export default function PackagesPage() {
  const { user } = useAuth();
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPkg, setSelectedPkg] = useState(null);
  
  // Booking modal for package
  const [bookingModalOpen, setBookingModalOpen] = useState(false);
  const [bookingDate, setBookingDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    return d.toISOString().split("T")[0];
  });
  const [bookingSlot, setBookingSlot] = useState("");
  const [patientName, setPatientName] = useState(user?.name || "");
  const [patientPhone, setPatientPhone] = useState(user?.phone || "+91 98300 ");
  const [patientEmail, setPatientEmail] = useState(user?.email || "");
  const [patientAge, setPatientAge] = useState(user?.age || 40);
  const [patientGender, setPatientGender] = useState(user?.gender || "Male");
  const [address, setAddress] = useState(user?.address || "Salt Lake / Kolkata");
  const [homeSample, setHomeSample] = useState(true);
  const [pincode, setPincode] = useState("");
  const [homeServiceable, setHomeServiceable] = useState(false);
  const [coupon, setCoupon] = useState(null);
  const quote = usePriceQuote({ amount: selectedPkg?.price || 0, bookingType: "package", email: patientEmail, enabled: !!selectedPkg });
  const memberDiscount = quote?.member_discount || 0;
  const pkgTotal = selectedPkg ? Math.max(0, selectedPkg.price - memberDiscount - (coupon?.discount || 0)) : 0;
  const [paymentMethod, setPaymentMethod] = useState("Razorpay");
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [confirmedBooking, setConfirmedBooking] = useState(null);
  const [payState, setPayState] = useState(null); const [payMessage, setPayMessage] = useState("");
  const intentRef = useRef(null);
  const [error, setError] = useState("");

  useEffect(() => {
    fetchPackages();
  }, []);
  useLiveEvents(["catalog"], () => fetchPackages());

  useEffect(() => {
    if (user) {
      if (!patientName) setPatientName(user.name);
      if (!patientEmail) setPatientEmail(user.email);
      if (user.phone && patientPhone.length <= 10) setPatientPhone(user.phone);
    }
  }, [user]);

  const fetchPackages = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/packages`);
      setPackages(res.data || []);
    } catch (err) {
      console.error("Error fetching packages:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenBooking = (pkg) => {
    setSelectedPkg(pkg);
    setConfirmedBooking(null); setPayState(null); setPayMessage(""); intentRef.current = null;
    setError("");
    setBookingModalOpen(true);
  };

  const handleConfirmPackageBooking = async (e) => {
    e.preventDefault();
    if (!patientName || !patientPhone || !patientEmail) {
      setError("Please fill in patient name, phone and email");
      return;
    }
    if (homeSample && (!homeServiceable || !bookingSlot)) {
      setError("Please enter a serviceable PIN code and pick an available home collection slot");
      return;
    }

    setIsSubmitting(true);
    setError("");
    try {
      const payload = {
        package_id: selectedPkg.id,
        package_name: selectedPkg.name,
        date: bookingDate,
        time_slot: bookingSlot,
        patient_name: patientName,
        patient_phone: patientPhone,
        patient_email: patientEmail,
        patient_age: Number(patientAge),
        patient_gender: patientGender,
        address: address,
        pincode: homeSample ? pincode : null,
        home_sample: homeSample,
        payment_method: paymentMethod,
        amount: selectedPkg.price,
        coupon_code: coupon?.code || null
      };

      if (paymentMethod === "Razorpay" && !user) { setError("Please sign in to pay online — your booking will be saved to My Health."); setIsSubmitting(false); return; }
      if (!intentRef.current) intentRef.current = await getBookingIntent();
      const tok = localStorage.getItem("sigmacare_token");
      const res = await axios.post(`${API}/bookings/package`, payload, { withCredentials: true, headers: { "Idempotency-Key": intentRef.current, ...(tok ? { Authorization: `Bearer ${tok}` } : {}) } });
      if (res.data.status !== "success") {
        setError("Failed to confirm package booking.");
        setIsSubmitting(false);
        return;
      }
      const booking = res.data.booking;
      setConfirmedBooking(booking);

      if (paymentMethod === "Razorpay") {
        setPayState(booking.payment_status === "Paid" ? "paid" : "pending"); setIsSubmitting(false);
        if (booking.payment_status === "Paid") return;
        await startRazorpayPayment({
          bookingId: booking.id,
          bookingType: "package",
          patient: { name: patientName, email: patientEmail, phone: patientPhone },
          centreName: "6SigmaCare Health Packages",
          onSuccess: () => { setPayState("paid"); setPayMessage(""); },
          onFailure: (err) => { setPayState("failed"); setPayMessage(err?.message || "Payment failed."); },
        });
      } else {
        setPayState(null);
        setIsSubmitting(false);
      }
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to confirm package booking.");
      setIsSubmitting(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-8 py-10 space-y-12">
      
      {/* Header Banner */}
      <div className="bg-[#EBF4F1] p-8 sm:p-12 rounded-3xl border border-[#3B8271]/20 text-left">
        <span className="text-[10px] font-bold uppercase tracking-wider text-[#0D3B2E] bg-white px-3 py-1 rounded-full border border-emerald-300 shadow-sm">
          6SigmaCare Kolkata Preventive Care
        </span>
        <h1 className="font-serif-luxury text-3xl sm:text-5xl text-[#0D3B2E] font-bold mt-2">
          Comprehensive Health Packages
        </h1>
        <p className="text-xs sm:text-sm text-gray-600 max-w-2xl leading-relaxed mt-2">
          Carefully formulated screening suites covering complete pathology panels, 3D/4D ultrasound, digital X-rays, 2D Echo Doppler and physician consultations with up to 58% package savings.
        </p>
      </div>

      {/* Packages Detailed List */}
      <div className="space-y-8">
        {packages.map((pkg) => (
          <div
            key={pkg.id}
            id={pkg.slug}
            data-testid={`package-section-${pkg.slug}`}
            className="bg-white rounded-3xl border-2 border-gray-100 hover:border-[#0D3B2E]/30 shadow-sm hover:shadow-xl transition-all p-6 sm:p-8"
          >
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
              
              {/* Left Column Info */}
              <div className="lg:col-span-8 space-y-4">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-[10px] font-bold uppercase tracking-wider bg-[#EBF4F1] text-[#0D3B2E] px-2.5 py-0.5 rounded-full">
                    {pkg.category}
                  </span>
                  <span className="text-[10px] font-semibold text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
                    Ideal for: {pkg.gender}
                  </span>
                  {pkg.popular && (
                    <span className="text-[10px] font-bold uppercase bg-amber-400 text-[#07221A] px-2.5 py-0.5 rounded-full">
                      Most Popular in Kolkata
                    </span>
                  )}
                </div>

                <h2 className="font-serif-luxury text-2xl sm:text-3xl font-bold text-gray-900">
                  {pkg.name}
                </h2>
                <p className="text-xs sm:text-sm text-gray-600 leading-relaxed">
                  {pkg.description}
                </p>

                <div className="p-3 bg-[#FBF9F4] rounded-2xl border border-gray-200 text-xs text-gray-700">
                  <p className="font-semibold text-gray-900">Who is this recommended for?</p>
                  <p className="text-[11px] text-gray-600 mt-0.5">{pkg.recommended_for}</p>
                </div>

                {/* Included Tests Breakdown */}
                <div className="space-y-2 pt-2">
                  <h4 className="text-xs font-bold text-gray-900 uppercase tracking-wider">
                    Included Diagnostic Investigations ({pkg.tests_count} Parameters):
                  </h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {pkg.included_tests.map((testName, i) => (
                      <div key={i} className="flex items-start gap-2 text-xs text-gray-700">
                        <CheckCircle2 className="w-4 h-4 text-[#3B8271] shrink-0 mt-0.5" />
                        <span>{testName}</span>
                      </div>
                    ))}
                  </div>
                  <RelatedHealthArticles kind="package" refId={pkg.id} title="Recommended reading" testId={`pkg-articles-${pkg.id}`} />
                  <ReviewsBlock entityType="package" entityId={pkg.id} title="Patient experiences" compact />
                  {(pkg.inclusions || []).length > 0 && (
                    <div className="pt-2" data-testid={`pkg-inclusions-${pkg.id}`}>
                      <h4 className="text-xs font-bold text-gray-900 uppercase tracking-wider">Also Included:</h4>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-1">
                        {pkg.inclusions.map((inc, i) => (
                          <div key={i} className="flex items-start gap-2 text-xs text-gray-700"><CheckCircle2 className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" /><span>{inc.name}</span></div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Right Column Pricing & Action */}
              <div className="lg:col-span-4 bg-[#FBF9F4] p-6 rounded-3xl border border-gray-200 space-y-5 text-center flex flex-col justify-between h-full">
                <div className="space-y-2">
                  <span className="text-[11px] text-gray-500 uppercase font-semibold">Special Package Offer</span>
                  <div>
                    <span className="text-sm text-gray-400 line-through mr-2">MRP ₹{pkg.mrp}</span>
                    <p className="font-serif-luxury text-4xl font-bold text-[#0D3B2E]">₹{pkg.price}</p>
                  </div>
                  <div className="inline-block bg-emerald-100 text-emerald-800 text-xs font-bold px-3 py-1 rounded-full">
                    Instant {pkg.discount_pct}% Discount
                  </div>
                </div>

                <div className="space-y-1.5 text-[11px] text-gray-600 text-left border-t border-gray-200 pt-3">
                  <p>• Fasting Required: <b>{pkg.fasting_required ? "Yes (10-12 Hours)" : "No"}</b></p>
                  <p>• Doorstep Home Collection: <b>Available in Kolkata</b></p>
                  <p>• Verified Digital Reports in <b>My Health</b></p>
                </div>

                {pkg.purchasable === false ? (
                  <div data-testid={`book-package-unavailable-${pkg.id}`} className="w-full py-3 rounded-2xl bg-gray-100 text-gray-600 text-xs font-semibold">Online booking not available yet — please contact the centre for pricing.</div>
                ) : (
                <button
                  onClick={() => handleOpenBooking(pkg)}
                  data-testid={`book-package-btn-${pkg.id}`}
                  className="w-full py-3.5 bg-[#0D3B2E] hover:bg-[#165644] text-white text-xs font-semibold rounded-2xl shadow-lg transition-all"
                >
                  Book Package Now
                </button>
                )}
              </div>

            </div>
          </div>
        ))}
      </div>

      {/* Package Booking Modal */}
      {bookingModalOpen && selectedPkg && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in">
          <div 
            data-testid="package-booking-modal"
            className="w-full max-w-lg bg-white rounded-3xl shadow-2xl border border-gray-100 overflow-hidden"
          >
            <div className="bg-[#0D3B2E] text-white p-6 relative">
              <button
                onClick={() => setBookingModalOpen(false)}
                className="absolute top-5 right-5 text-gray-300 hover:text-white"
              >
                <X className="w-5 h-5" />
              </button>
              <span className="text-[10px] font-bold uppercase tracking-wider bg-amber-400 text-[#07221A] px-2 py-0.2 rounded">
                Package Booking
              </span>
              <h3 className="font-serif-luxury text-xl font-bold text-white mt-1">{selectedPkg.name}</h3>
              <p className="text-xs text-emerald-200" data-testid="pkg-header-price">{confirmedBooking ? `${confirmedBooking.payment_status === "Paid" ? "Paid" : "Payable"}: ₹${confirmedBooking.amount}${confirmedBooking.discount ? ` (coupon ${confirmedBooking.coupon_code} −₹${confirmedBooking.discount})` : ""}` : coupon ? `Payable: ₹${pkgTotal} (coupon ${coupon.code} −₹${coupon.discount})` : `Total Price: ₹${selectedPkg.price} (Save ${selectedPkg.discount_pct}%)`}</p>
            </div>

            <div className="p-6 max-h-[70vh] overflow-y-auto">
              {error && (
                <div className="mb-4 p-3 bg-rose-50 border border-rose-200 text-rose-800 text-xs rounded-xl flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 shrink-0" />
                  <span>{error}</span>
                </div>
              )}

              {!confirmedBooking ? (
                <form onSubmit={handleConfirmPackageBooking} className="space-y-4">
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">Patient Full Name *</label>
                    <input
                      type="text"
                      required
                      value={patientName}
                      onChange={(e) => setPatientName(e.target.value)}
                      data-testid="pkg-patient-name"
                      className="w-full px-3 py-2 bg-[#FBF9F4] border border-gray-200 rounded-xl text-xs"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-1">Phone *</label>
                      <input
                        type="tel"
                        required
                        value={patientPhone}
                        onChange={(e) => setPatientPhone(e.target.value)}
                        data-testid="pkg-patient-phone"
                        className="w-full px-3 py-2 bg-[#FBF9F4] border border-gray-200 rounded-xl text-xs"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-1">Email *</label>
                      <input
                        type="email"
                        required
                        value={patientEmail}
                        onChange={(e) => setPatientEmail(e.target.value)}
                        data-testid="pkg-patient-email"
                        className="w-full px-3 py-2 bg-[#FBF9F4] border border-gray-200 rounded-xl text-xs"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-gray-800 uppercase tracking-wider mb-2">Sample Collection</label>
                    <div className="grid grid-cols-2 gap-3">
                      <button type="button" data-testid="pkg-type-home" onClick={() => { setHomeSample(true); setBookingSlot(""); }} className={`p-3 rounded-2xl border text-left flex items-start gap-2.5 ${homeSample ? "border-[#0D3B2E] bg-[#EBF4F1] ring-1 ring-[#0D3B2E]" : "border-gray-200"}`}>
                        <Home className="w-5 h-5 text-[#3B8271] shrink-0 mt-0.5" /><div><p className="text-xs font-bold text-gray-900">Home Collection</p><p className="text-[11px] text-gray-500">Instant slot by PIN code</p></div>
                      </button>
                      <button type="button" data-testid="pkg-type-centre" onClick={() => { setHomeSample(false); setBookingSlot("07:30 AM - 08:30 AM (Fasting)"); }} className={`p-3 rounded-2xl border text-left flex items-start gap-2.5 ${!homeSample ? "border-[#0D3B2E] bg-[#EBF4F1] ring-1 ring-[#0D3B2E]" : "border-gray-200"}`}>
                        <Building2 className="w-5 h-5 text-[#0D3B2E] shrink-0 mt-0.5" /><div><p className="text-xs font-bold text-gray-900">Centre Visit</p><p className="text-[11px] text-gray-500">Salt Lake, Kolkata</p></div>
                      </button>
                    </div>
                  </div>

                  {homeSample ? (
                    <HomeCollectionSlotPicker
                      idPrefix="pkg-hc"
                      pincode={pincode}
                      onPincodeChange={setPincode}
                      date={bookingDate}
                      onDateChange={setBookingDate}
                      slot={bookingSlot}
                      onSlotChange={setBookingSlot}
                      onServiceability={setHomeServiceable}
                    />
                  ) : (
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-1">Preferred Date</label>
                      <input
                        type="date"
                        min={new Date().toISOString().split("T")[0]}
                        value={bookingDate}
                        onChange={(e) => setBookingDate(e.target.value)}
                        data-testid="pkg-date-input"
                        className="w-full px-3 py-2 bg-[#FBF9F4] border border-gray-200 rounded-xl text-xs"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-1">Slot</label>
                      <select
                        value={bookingSlot}
                        onChange={(e) => setBookingSlot(e.target.value)}
                        data-testid="pkg-slot-select"
                        className="w-full px-3 py-2 bg-[#FBF9F4] border border-gray-200 rounded-xl text-xs"
                      >
                        <option value="07:30 AM - 08:30 AM (Fasting)">07:30 AM - 08:30 AM (Fasting)</option>
                        <option value="08:30 AM - 09:30 AM (Fasting)">08:30 AM - 09:30 AM (Fasting)</option>
                        <option value="09:30 AM - 10:30 AM">09:30 AM - 10:30 AM</option>
                        <option value="11:00 AM - 12:00 PM">11:00 AM - 12:00 PM</option>
                      </select>
                    </div>
                  </div>
                  )}

                  {homeSample && (
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">Kolkata Address (For Home Phlebotomy)</label>
                    <input
                      type="text"
                      value={address}
                      onChange={(e) => setAddress(e.target.value)}
                      data-testid="pkg-address-input"
                      className="w-full px-3 py-2 bg-[#FBF9F4] border border-gray-200 rounded-xl text-xs"
                      placeholder="Street, Salt Lake, Kolkata"
                    />
                  </div>
                  )}

                  {/* Payment */}
                  <div>
                    <label className="block text-xs font-bold text-gray-800 uppercase tracking-wider mb-1.5">
                      Payment Choice
                    </label>
                    <div className="space-y-2">
                      <label className={`p-2.5 rounded-xl border flex items-center justify-between cursor-pointer text-xs ${paymentMethod === "Razorpay" ? "border-[#0D3B2E] bg-[#EBF4F1]" : "border-gray-200"}`}>
                        <span className="flex items-center gap-2">
                          <input type="radio" data-testid="pkg-pay-option-razorpay" checked={paymentMethod === "Razorpay"} onChange={() => setPaymentMethod("Razorpay")} />
                          <b>Razorpay Online (UPI / Card)</b>
                        </span>
                        <CreditCard className="w-4 h-4 text-[#3B8271]" />
                      </label>
                      <label className={`p-2.5 rounded-xl border flex items-center justify-between cursor-pointer text-xs ${paymentMethod === "Pay at Centre" ? "border-[#0D3B2E] bg-[#EBF4F1]" : "border-gray-200"}`}>
                        <span className="flex items-center gap-2">
                          <input type="radio" data-testid="pkg-pay-option-centre" checked={paymentMethod === "Pay at Centre"} onChange={() => setPaymentMethod("Pay at Centre")} />
                          <b>Pay at Desk / Cash at Collection</b>
                        </span>
                        <Building2 className="w-4 h-4 text-gray-500" />
                      </label>
                    </div>
                  </div>

                  <MemberDiscountLine quote={quote} testId="pkg-member-line" />
                  <CouponInput idPrefix="pkg-coupon" amount={selectedPkg.price - memberDiscount} bookingType="package" email={patientEmail} applied={coupon} onApplied={setCoupon} />

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    data-testid="confirm-package-booking-btn"
                    className="w-full py-3 bg-[#0D3B2E] text-white rounded-xl text-xs font-semibold hover:bg-[#165644] flex items-center justify-center gap-2"
                  >
                    {isSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4 text-amber-300" />}
                    Confirm Package Booking (₹{pkgTotal})
                  </button>
                </form>
              ) : confirmedBooking.payment_method === "Razorpay" ? (
                <BookingPaymentOutcome kind="package" booking={confirmedBooking} state={payState || "pending"} message={payMessage} patient={{ name: patientName, email: patientEmail, phone: patientPhone }} centreName="6SigmaCare Health Packages" onClose={() => setBookingModalOpen(false)} closeLabel="Done" testId="pkg-outcome" />
              ) : (
                <div className="text-center py-4 space-y-4">
                  <div className="w-14 h-14 bg-emerald-100 text-[#0D3B2E] rounded-full flex items-center justify-center mx-auto">
                    <CheckCircle2 className="w-8 h-8" />
                  </div>
                  <h4 className="font-serif-luxury text-xl font-bold text-[#0D3B2E]">Package Confirmed!</h4>
                  <p className="text-xs text-gray-600">Booking Ref: <b className="font-mono">{confirmedBooking.id}</b></p>
                  <p className="text-xs text-gray-800 font-semibold" data-testid="pkg-confirmed-amount">Amount: ₹{confirmedBooking.amount}{confirmedBooking.discount ? <span className="text-emerald-700" data-testid="pkg-coupon-line"> · {confirmedBooking.coupon_code} saved ₹{confirmedBooking.discount}</span> : null} · {confirmedBooking.payment_status}</p>
                  {confirmedBooking.phlebotomist_name && (
                    <p className="text-xs text-[#0D3B2E] font-semibold" data-testid="pkg-confirmed-phlebotomist">Phlebotomist {confirmedBooking.phlebotomist_name} • {confirmedBooking.date}, {confirmedBooking.time_slot} • PIN {confirmedBooking.pincode}</p>
                  )}
                  <p className="text-xs text-emerald-800 bg-emerald-50 p-3 rounded-xl">
                    Our phlebotomist & care coordinator will contact you at {confirmedBooking.patient_phone} prior to the appointment.
                  </p>
                  <button
                    onClick={() => setBookingModalOpen(false)}
                    className="w-full py-2.5 bg-[#0D3B2E] text-white rounded-xl text-xs font-semibold"
                  >
                    Done
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
